import type { TmsInvoiceRow, TmsLoadsReadiness } from '@numeo/types-main-web-app'
import { create } from 'zustand'
import { readCard } from '../api/writeCardApi'

/** The most load numbers one readiness read takes (the card's own input bound). */
const READ_LOAD_NUMBERS_MAX = 50

interface RowsState {
    /** The latest invoicing row of each load, by load number. A write result patches its row here. */
    rows: Record<string, TmsInvoiceRow>
    /** Puts rows in the store, keyed by each load number a row names. Rows already there are replaced. */
    patch: (rows: TmsInvoiceRow[]) => void
    /** Drops the stored rows of loads, so a load that is no longer an invoicing row (not delivered, not found, ambiguous) is not shown as one. */
    forget: (loadNumbers: string[]) => void
    /** Reads the readiness of the named loads (in batches of 50) and patches every row they answer with. */
    refresh: (loadNumbers: string[]) => Promise<TmsLoadsReadiness[]>
    /** Forgets every row, for another user or company in the same tab (a logout does not reload the page); reads still running are dropped. */
    reset: () => void
}

/**
 * The latest change asked for each load number: a read takes a ticket per load when it starts, and a write result (`patch`) takes one
 * too. A read that answers after a newer ticket was taken for a load leaves that load alone, so an older read never puts a draft row
 * back over the sent one a write just stored. `reset` clears it, which drops every read still running.
 */
let tickets = new Map<string, number>()
let lastTicket = 0
/** Bumped by `reset`: a read started before it answers for the previous owner and is dropped. */
let epoch = 0
const takeTickets = (loadNumbers: string[]): number => {
    lastTicket++
    for (const loadNumber of loadNumbers) tickets.set(loadNumber, lastTicket)
    return lastTicket
}

/** The invoicing rows of one readiness answer: the ready, the needs-documents, the invoiced and the Amazon-billed ones. */
export const invoicingRowsOf = (readiness: TmsLoadsReadiness): TmsInvoiceRow[] => [
    ...readiness.ready,
    ...readiness.needs_documents,
    ...readiness.invoiced.rows,
    ...readiness.billed_through_amazon.rows,
]

/** Splits a list into pieces of at most `size`, keeping the order. */
export const chunked = <T>(items: T[], size: number): T[][] => {
    const pieces: T[][] = []
    for (let index = 0; index < items.length; index += size) pieces.push(items.slice(index, index + size))
    return pieces
}

/** Keyed by load number, so a table row, a drawer and a selection read the same row. Uses the card's readiness read; no other source. */
export const useInvoicingRowsStore = create<RowsState>()((set) => {
    /** Writes the rows and drops the gone loads, each only where `isCurrent` says no newer change was asked for that load. */
    const apply = (rows: TmsInvoiceRow[], gone: string[], isCurrent: (loadNumber: string) => boolean) =>
        set((state) => {
            const next = { ...state.rows }
            for (const row of rows) for (const loadNumber of row.load_numbers) if (isCurrent(loadNumber)) next[loadNumber] = row
            for (const loadNumber of gone) if (isCurrent(loadNumber)) delete next[loadNumber]
            return { rows: next }
        })
    return {
        rows: {},
        patch: (rows) => {
            takeTickets(rows.flatMap((row) => row.load_numbers))
            apply(rows, [], () => true)
        },
        forget: (loadNumbers) => {
            takeTickets(loadNumbers)
            apply([], loadNumbers, () => true)
        },
        refresh: async (loadNumbers) => {
            const answers: TmsLoadsReadiness[] = []
            for (const piece of chunked(loadNumbers, READ_LOAD_NUMBERS_MAX)) {
                const ticket = takeTickets(piece)
                const started = epoch
                const readiness = await readCard<TmsLoadsReadiness>('tms_invoices.worklist', { load_numbers: piece })
                answers.push(readiness)
                const rows = invoicingRowsOf(readiness)
                // A requested load the answer puts in none of its invoicing lists (not delivered, not found, ambiguous, unread) has no row now.
                const answered = new Set(rows.flatMap((row) => row.load_numbers))
                const gone = piece.filter((loadNumber) => !answered.has(loadNumber))
                // A load another read or a write result asked for after this read started keeps that newer row.
                apply(rows, gone, (loadNumber) => started === epoch && (tickets.get(loadNumber) ?? 0) <= ticket)
            }
            return answers
        },
        reset: () => {
            epoch++
            tickets = new Map()
            set({ rows: {} })
        },
    }
})
